import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Button, TextField, Paper, Grid, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import get from 'lodash/get';

import * as API from 'api/groups';
import InputLabel from 'components/InputLabel';
import { createGroup, deleteGroup, updateGroup } from 'store/ecosystems/actions';
import { createPolicyObject } from 'store/objects/actions';
import { validationSchema } from './validationSchema';

const GroupForm = ({ object, policyField, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();

  const handleSave = async (values) => {
    try {
      if (object?.uuid) {
        const result = await API.updateGroup({ customerUUID, ecosystemUUID, payload: values, uuid: object.uuid });
        dispatch(updateGroup({ group: result }));
        enqueueSnackbar(`${values.name} (Profile Group) has been updated`, { variant: 'success' });
      } else {
        const result = await API.createGroup({ customerUUID, ecosystemUUID, payload: values });
        result.element = 'group';
        dispatch(createGroup({ group: result }));
        if (policyField) {
          dispatch(createPolicyObject({ field: policyField, object: result }));
        }
        enqueueSnackbar('New Profile Group has been created', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error saving group:', error);
      dispatch({ type: 'GROUP_FAILURE', payload: { message: error } });
    }
  };

  const { values, errors, touched, handleChange, handleSubmit } = useFormik({
    initialValues: {
      name: get(object, 'name', ''),
    },
    validationSchema,
    onSubmit: handleSave,
  });

  const handleDelete = async () => {
    if (!object?.uuid) return;
    try {
      await API.deleteGroup({ customerUUID, ecosystemUUID, uuid: object.uuid });
      dispatch(deleteGroup({ ecosystemUUID, uuid: object.uuid }));
      enqueueSnackbar(`${object.name} (Profile Group) has been removed`, { variant: 'success' });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error deleting object:', error);
      dispatch({ type: 'GROUP_DELETE_FAILURE', payload: { message: error } });
    }
  };

  return (
    <div>
      <Box sx={{ px: 4, pt: 2, pb: 3 }}>
        <Paper elevation={0} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <InputLabel required>Name</InputLabel>
                <TextField
                  fullWidth
                  name="name"
                  variant="standard"
                  placeholder="Name"
                  value={values.name}
                  onChange={handleChange}
                  error={Boolean(touched.name && errors.name)}
                  helperText={!!touched.name && errors.name}
                  inputProps={{ 'data-testid': 'input-profile_group' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <Box sx={{ pt: 3 }}>
          <Stack direction="row-reverse" spacing={2} justifyContent="space-between">
            <Button variant="contained" onClick={handleSubmit} sx={{ minWidth: 90 }} data-testid="btn-save_group">
              {object?.uuid ? 'Save' : 'Add'}
            </Button>
            {object?.uuid && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ minWidth: 90 }}
                data-testid="btn-delete_group"
              >
                Delete
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

GroupForm.propTypes = {
  object: PropTypes.object,
  onClose: PropTypes.func,
  policyField: PropTypes.string,
};

export default GroupForm;
